import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  CardBody,
  CardHeader,
  Container,
  Row,
  Col,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Alert,
  Spinner,
  Badge
} from 'reactstrap';
import axiosInstance from '../../api/Axios';

const GenerateVouchers = () => {
  const { beneficiaryId } = useParams();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    start_date: '',
    end_date: '',
    amount: '',
    currency: 'ZAR'
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [beneficiaryInfo, setBeneficiaryInfo] = useState(null);
  const [generatedVoucher, setGeneratedVoucher] = useState(null);

  // Load beneficiary information
  useEffect(() => {
    const loadBeneficiaryInfo = async () => {
      try {
        const response = await axiosInstance.get(`/accounts/beneficiaries/${beneficiaryId}/`);
        setBeneficiaryInfo(response.data);
      } catch (err) {
        console.error('Error loading beneficiary info:', err);
        setError('Failed to load beneficiary information');
      }
    };

    if (beneficiaryId) {
      loadBeneficiaryInfo();
    }
  }, [beneficiaryId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear any previous errors when user starts typing
    if (error) setError('');
  };

  const validateForm = () => {
    if (!formData.start_date) {
      setError('Start date is required');
      return false;
    }
    
    if (!formData.end_date) {
      setError('End date is required');
      return false;
    }
    
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      setError('Amount must be greater than 0');
      return false;
    }
    
    const startDate = new Date(formData.start_date);
    const endDate = new Date(formData.end_date);
    
    if (endDate <= startDate) {
      setError('End date must be after start date');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const voucherData = {
        beneficiary: parseInt(beneficiaryId),
        start_date: formData.start_date,
        end_date: formData.end_date,
        amount: formData.amount,
        currency: formData.currency
      };

      const response = await axiosInstance.post('/accounts/vouchers/', voucherData);
      
      setGeneratedVoucher(response.data);
      setSuccess('Accommodation voucher generated successfully!');
      
      // Clear form
      setFormData({
        start_date: '',
        end_date: '',
        amount: '',
        currency: 'ZAR'
      });

    } catch (err) {
      console.error('Error generating voucher:', err);
      if (err.response?.data) {
        const errorMessages = Object.values(err.response.data).flat();
        setError(errorMessages.join(', '));
      } else {
        setError('Failed to generate voucher. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  return (
    <Container fluid>
      <Row>
        <Col lg={8} className="mx-auto">
          {/* Beneficiary Info Card */}
          {beneficiaryInfo && (
            <Card className="mb-4">
              <CardHeader className="bg-info text-white">
                <h6 className="mb-0">
                  <i className="fas fa-user me-2"></i>
                  Beneficiary Information
                </h6>
              </CardHeader>
              <CardBody>
                <Row>
                  <Col md={6}>
                    <strong>Name:</strong> {beneficiaryInfo.name || 'N/A'}
                  </Col>
                  <Col md={6}>
                    <strong>Institution:</strong> {beneficiaryInfo.institution_name || 'N/A'}
                  </Col>
                  <Col md={6}>
                    <strong>Bursary Code:</strong> <Badge color="secondary">{beneficiaryInfo.beneficiary_bursary_code || 'N/A'}</Badge>
                  </Col>
                  <Col md={6}>
                    <strong>Currency:</strong> {beneficiaryInfo.bursary_currency || 'ZAR'}
                  </Col>
                </Row>
              </CardBody>
            </Card>
          )}

          {/* Voucher Generation Form */}
          <Card>
            <CardHeader>
              <h4 className="mb-0">
                <i className="fas fa-home me-2"></i>
                Generate Accommodation Voucher
              </h4>
            </CardHeader>
            <CardBody>
              {error && (
                <Alert color="danger">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  {error}
                </Alert>
              )}

              {success && !generatedVoucher && (
                <Alert color="success">
                  <i className="fas fa-check-circle me-2"></i>
                  {success}
                </Alert>
              )}

              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="start_date">
                        <i className="fas fa-calendar-alt me-1"></i>
                        Booking Start Date *
                      </Label>
                      <Input
                        type="date"
                        name="start_date"
                        id="start_date"
                        value={formData.start_date}
                        onChange={handleInputChange}
                        min={getTodayDate()}
                        required
                      />
                      <small className="text-muted">Students can book accommodation from this date</small>
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="end_date">
                        <i className="fas fa-calendar-alt me-1"></i>
                        Booking End Date *
                      </Label>
                      <Input
                        type="date"
                        name="end_date"
                        id="end_date"
                        value={formData.end_date}
                        onChange={handleInputChange}
                        min={formData.start_date || getTodayDate()}
                        required
                      />
                      <small className="text-muted">Students can book accommodation until this date</small>
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md={8}>
                    <FormGroup>
                      <Label for="amount">
                        <i className="fas fa-money-bill-wave me-1"></i>
                        Voucher Amount *
                      </Label>
                      <Input
                        type="number"
                        name="amount"
                        id="amount"
                        value={formData.amount}
                        onChange={handleInputChange}
                        placeholder="Enter amount (e.g., 5000.00)"
                        step="0.01"
                        min="0.01"
                        required
                      />
                    </FormGroup>
                  </Col>
                  <Col md={4}>
                    <FormGroup>
                      <Label for="currency">
                        <i className="fas fa-coins me-1"></i>
                        Currency
                      </Label>
                      <Input
                        type="select"
                        name="currency"
                        id="currency"
                        value={formData.currency}
                        onChange={handleInputChange}
                      >
                        <option value="ZAR">ZAR (South African Rand)</option>
                        <option value="USD">USD (US Dollar)</option>
                        <option value="EUR">EUR (Euro)</option>
                      </Input>
                    </FormGroup>
                  </Col>
                </Row>

                <div className="text-center">
                  <Button
                    type="submit"
                    color="success"
                    size="lg"
                    disabled={loading}
                    className="px-5"
                  >
                    {loading ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Generating Voucher...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-ticket-alt me-2"></i>
                        Generate Accommodation Voucher
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </CardBody>
          </Card>

          {/* Generated Voucher Display */}
          {generatedVoucher && (
            <Card className="mt-4 border-success">
              <CardHeader className="bg-success text-white">
                <h5 className="mb-0">
                  <i className="fas fa-check-circle me-2"></i>
                  Voucher Generated Successfully!
                </h5>
              </CardHeader>
              <CardBody>
                <Alert color="success" className="mb-3">
                  <h6 className="alert-heading">
                    <i className="fas fa-home me-2"></i>
                    Accommodation Voucher Details
                  </h6>
                  <hr />
                  <Row className="mb-0">
                    <Col md={6}>
                      <strong>Voucher Code:</strong>
                      <br />
                      <Badge color="primary" className="fs-6 p-2 font-monospace">
                        {generatedVoucher.voucher_code}
                      </Badge>
                    </Col>
                    <Col md={6}>
                      <strong>Amount:</strong>
                      <br />
                      <span className="fs-5 text-success">
                        {generatedVoucher.currency} {parseFloat(generatedVoucher.amount).toLocaleString('en-ZA', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                      </span>
                    </Col>
                  </Row>
                  <Row className="mt-3">
                    <Col md={6}>
                      <strong>Booking Valid From:</strong> {generatedVoucher.start_date}
                    </Col>
                    <Col md={6}>
                      <strong>Booking Valid Until:</strong> {generatedVoucher.end_date}
                    </Col>
                  </Row>
                  <Row className="mt-3">
                    <Col md={6}>
                      <strong>Beneficiary:</strong> {beneficiaryInfo?.name}
                    </Col>
                    <Col md={6}>
                      <strong>Created:</strong> {new Date(generatedVoucher.created_at).toLocaleString()}
                    </Col>
                  </Row>
                </Alert>

                <div className="text-center">
                  <p className="text-muted small">
                    <i className="fas fa-info-circle me-1"></i>
                    This voucher code will be linked to the student's ID number for verification when they login and attempt to use it.
                  </p>
                  
                  <Button
                    color="primary"
                    onClick={() => setGeneratedVoucher(null)}
                    className="me-2"
                  >
                    <i className="fas fa-plus me-1"></i>
                    Generate Another Voucher
                  </Button>
                  
                  <Button
                    color="secondary"
                    onClick={() => navigate(-1)}
                  >
                    <i className="fas fa-arrow-left me-1"></i>
                    Back to Beneficiary
                  </Button>
                </div>
              </CardBody>
            </Card>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default GenerateVouchers;